import Lenis from 'lenis'
import { Metrics } from '../core/Metrics';
import { isReducedMotion, isSmartphone, isTouch } from '../core/Basics';
import Scroll_Item from './Scroll_Item';
import { Functions } from '../utils/Functions';
import { Maths } from '../utils/Maths';

export class Scroll {
  static isEnabled = false;
  static lenis = null;
  static rafId = null;

  static defaultOptions = {
    autoRaf: false,
    smooth: true,
    lerp: 0.1,
    // Lenis té el seu propi listener de resize + ResizeObserver, independent del de
    // Metrics.js. Si el deixem actiu, torna a recalcular el límit d'scroll cada cop
    // que la barra de Safari amaga/mostra (el mateix problema que Metrics.js ja
    // filtra). El desactivem i deixem que sigui Scroll.resize() (cridat només quan
    // Metrics.js detecta un canvi real d'amplada) qui l'actualitzi explícitament.
    autoResize: false,
  };

  static observerOptions = {
    rootMargin: '0px 0px 0px 0px',
    threshold: 0,
  };

  static total_items = 0;
  static _items = [];
  static _itemsAuto = [];
  static _itemsObserver = {};
  static _classItems = [];
  static _insidersItems = [];

  static set y(n) { this.lenis?.scrollTo(n); }
  // A #Main (wrapper != window, veure Home/Default beforeShow) el scrollTop natiu d'iOS
  // pot anar per sota de 0 o per sobre del límit durant el rebot elàstic tàctil — a
  // diferència del scroll del document, que iOS sempre reporta clampat encara que
  // visualment reboti. Com que animatedScroll és un simple mirall del scrollTop natiu
  // (syncTouch:false → Lenis no intercepta el tàctil), aquest valor "fugit" es filtrava
  // sense clampar cap a --y, i els elements que en depenen 1:1 (--speed:-1, com
  // .text.__textShow a billboard-project) "rebotaven" visiblement més que la resta.
  // Clampem aquí, a la font, perquè ho aprofiti tothom qui llegeix Scroll.y.
  static get y() { return Maths.clamp(this.lenis?.animatedScroll ?? 0, 0, this.lenis?.limit ?? Infinity); }
  static get limit() { return this.lenis?.limit; }
  static get progress() { return this.lenis?.progress; }
  static get direction() { return this.lenis?.direction; }
  static get actualScroll() { return this.lenis?.actualScroll; }
  static get speed() { return this.lenis?.velocity; }
  static get lastSpeed() { return this.lenis?.lastVelocity; }
  static get isScrolling() { return this.lenis?.isScrolling; }

  static init(options = {}, isStopped = false) {
    if (!this.lenis) {
      this.lenis = new Lenis({ ...this.defaultOptions, ...options });
      this.lenis.on('scroll', () => this.updateItems());
    }

    this.lenis.scrollTo(0, { immediate: true });
    if (isStopped) this.stop();

    this.setupObserver();

    setTimeout(() => {
      this._addAll();
      this.resize();
    }, 100);

    this.isEnabled = true;
  }

  static _registerClass(id, klass) {
    this._classItems.push({ id, class: klass });
  }

  static _getClass(item) {
    const idClass = item.getAttribute('data-class') || 'default';

    for (const entry of this._classItems) {
      if (entry.id === idClass) return entry.class;
    }

    if (item.getAttribute('data-class')) {
      console.warn(`scroll-item [${idClass}] no existe, posiblemente no hayas hecho el import`);
    }

    return Scroll_Item;
  }

  static _getClassVS(__item, __default) {
    let idClass = __item.getAttribute('data-class') || 'default';

    for (let i = 0, j = Scroll._classItems.length; i < j; i++) {
      if (idClass === Scroll._classItems[i].id) {
        return Scroll._classItems[i].class;
      }
    }

    if (__item.getAttribute('data-class')) {
      console.warn(`scroll-item [${__item.getAttribute('data-class')}] no existe, posiblemente no hayas hecho el import`);
    }

    return __default || VScroll_Item;
  }

  static _registerInsider(insider) {
    this._insidersItems.push({ id: insider.selector, class: insider });
  }

  static setScrollbar(scrollbar) { }

  static setupObserver() {
    if (this._observer) this._observer.disconnect();

    if (!isReducedMotion) {
      // WebKit (Safari i "Chrome" a iOS — per política d'Apple totes dues corren sobre
      // el motor de Safari) té un bug conegut: amb `root` implícit (null = document),
      // l'IntersectionObserver NO detecta interseccions d'elements que pengen d'un
      // ancestor amb el seu propi scroll (overflow:auto) — exactament el cas de
      // Home/Product des que Lenis fa servir #Main com a wrapper en lloc de window
      // (veure Home.beforeShow / Default.beforeShow). Blink (Chrome d'escriptori,
      // INCLÒS el simulador mòbil) no té aquest bug, per això allà sí funcionava.
      // Solució: passar `root` explícit igual al wrapper real quan no és window.
      const wrapper = this.lenis?.options?.wrapper;
      const root = wrapper && wrapper !== window ? wrapper : null;

      this._observer = new IntersectionObserver(
        entries => this.checkObserver(entries),
        { ...this.observerOptions, root }
      );
    }
  }

  static checkObserver(entries) {
    for (const entry of entries) {
      const id = Functions.getId(entry.target);
      const item = this._itemsObserver[id];
      entry.isIntersecting ? item.show() : item.hide();
    }
  }

  static start() {
    this.lenis?.start();
  }

  static stop() {
    this.lenis?.stop();
  }

  static loop(time) {
    if (this.lenis) {
      this.lenis.raf(time * 1000);
    }
  }

  static updateItems() {
    for (let i = 0; i < this.total_items; i++) {
      this._items[i].update();
    }
  }

  static resize() {
    this.lenis?.resize(); // autoResize:false → cal disparar-ho manualment aquí (veure defaultOptions)
    this._items.forEach(item => {
      item.resize();
      item.resizeLimits(Metrics.WIDTH, Metrics.HEIGHT);
    });
  }

  static show() { }
  static hide() { }

  // Scroll navigation
  static goto(n) {
    this.lenis.scrollTo(n);
  }

  static directGoto(n) {
    this.lenis.scrollTo(n, { offset: -Metrics.OFFSET, immediate: true });
  }

  static gotoDOMElement(el) {
    this.lenis.scrollTo(el, { offset: -Metrics.OFFSET });
  }

  static gotoID(id) {
    this.lenis.scrollTo(`#${id}`, { offset: -Metrics.OFFSET });
  }

  static gotoAnchor(anchor) {
    this.gotoID(anchor.startsWith('#') ? anchor.substring(1) : anchor);
  }

  static gotoPercentage(n) {
    this.lenis.scrollTo(this.lenis.limit * n, { offset: -Metrics.OFFSET });
  }

  static _addAll(selector = "[scroll-item]") {
    const items = document.querySelectorAll(selector);

    for (const item of items) {
      item.setAttribute("scroll-item-loaded", "");
      item.removeAttribute("scroll-item");

      const allowMobile = isTouch ? item.getAttribute("data-avoid-mobile") === null : true;
      const allowSmartphone = isSmartphone ? item.getAttribute("data-avoid-smartphone") === null : true;
      const allowMove = item.getAttribute("data-scroll-auto") === null;

      if (allowMobile && allowSmartphone) {
        const Class = this._classItems.length > 0 ? this._getClass(item) : Scroll_Item;
        const instance = new Class(item, this.total_items, this);

        if (allowMove)
          this.total_items = this._items.push(instance);
        else
          this._itemsAuto.push(instance);

        this._itemsObserver[instance.id] = instance;

        if (!isReducedMotion) {
          this._observer.observe(instance.item);
        }
      }
    }
  }

  static dispose() {
    this.lenis?.destroy();
    this.lenis = null;

    this._observer?.disconnect();
    this._observer = null;

    for (const item of this._items) { item.dispose(); }
    for (const item of this._itemsAuto) { item.dispose(); }

    this._items = [];
    this._itemsAuto = [];
    this.total_items = 0;
    this._itemsObserver = {};
  }
}
